import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PortabilityRequestResponse } from 'src/app/domains/dids/models';
import { DID, DidByCompanyIdResponse, DID_CLASS, VerificationData } from 'voxloud-types/dist/models';
import { PortabilityService } from 'src/app/domains/dids/services/portability.service';
import { forkJoin, Subscription } from 'rxjs';
import { DidsService, VerificationDataService } from 'src/app/domains/dids/services';
import { VerificationDataRequirements } from 'ng-voxloud';
import { VDFormValue } from 'src/app/shared/components/verfication-data-form/verfication-data-form.component';
import { concatMap } from 'rxjs/operators';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { OPTION_TYPE } from 'src/app/shared/components';
import { EstimationRequest, PRODUCT_TYPE } from 'src/app/domains/payment/models/payment.model';
import { UntypedFormGroup } from '@angular/forms';
import { SubscriptionService } from 'src/app/domains/subscription/services';
import { Subscription as SubscriptionModel } from 'src/app/domains/subscription/models';

enum STEP {
  SELECT_TYPE = 'select-type',
  ADD_DATA = 'add-data',
  RECAP = 'recap'
};

@Component({
  selector: 'gdp-activate-testing-did-popup',
  templateUrl: './activate-testing-did-popup.component.html',
  styleUrls: ['./activate-testing-did-popup.component.scss']
})
export class ActivateTestingDidPopupComponent implements OnInit, OnDestroy {
  public language: string;
  public selection: 'vd' | 'portability';
  public did: DID;
  public companyId: number;
  public product: EstimationRequest;
  public subscription: SubscriptionModel;
  public form: UntypedFormGroup;
  public currentStep: STEP;
  public STEPS = STEP;
  public requirements: VerificationDataRequirements;
  public isVDRequired: boolean;
  public didsWithVerificationData: DID[] = [];
  public vdForm: VDFormValue = {
    type: '',
    isValid: false,
    value: null
  };
  public selectedOpt: { type: OPTION_TYPE, did: DID };
  public portabilityDocsForm = {
    files: null,
    isValid: false
  };
  public checkboxesData = {
    value: null,
    isValid: false
  };
  public loading = {
    vd: false,
    submit: false,
    subscription: false,
  };
  public planAddonHandling: string;
  private languageSubscription: Subscription;
  @Output() onEmitValue: EventEmitter<string> = new EventEmitter();

  constructor(
    private activeModal: NgbActiveModal,
    private portabilityService: PortabilityService,
    private verificationDataService: VerificationDataService,
    private didsService: DidsService,
    private translate: TranslateService,
    private subscriptionService: SubscriptionService
  ) { }

  ngOnInit(): void {
    this.language = this.translate.currentLang;
    this.languageSubscription = this.translate.onLangChange.subscribe((l: LangChangeEvent) => {
      this.language = l.lang;
    });
    this.getSubscription();
    this.currentStep = this.STEPS.SELECT_TYPE;
    this.selection = 'vd';
  }

  public onFormChange(event): void {
    this.vdForm = {
      isValid: event.isValid,
      value: event.value,
      type: event.type
    };
    this.selectedOpt = event.selectedOpt;
  }

  public get canSubmit(): boolean {
    let result: boolean = false;
    switch (this.selection) {
      case 'vd': {
        result = this.vdForm.isValid || !this.isVDRequired;
      };
        break;
      case 'portability': {
        result = this.portabilityDocsForm.isValid;
      };
        break;
      default: break;
    }
    return result;
  }

  public onSubmit(): void {
    if (this.selection === 'vd' && !this.isVDRequired) {
      this.activateDid();
    } else if (this.selection === 'vd' && this.isVDRequired) {
      if (this.selectedOpt.type === OPTION_TYPE.NEW) {
        this.activateWithVDCreation();
      } else {
        this.activateWithVD();
      }
    } else {
      this.activateWithPortabilityReq();
    }
  }

  public onGoNext(step): void {
    if (step === this.STEPS.RECAP) {
      this.product = {
        product: this.did.did_class === DID_CLASS.VOICE ? PRODUCT_TYPE.phone_number : PRODUCT_TYPE.fax,
        default_addon: this.did.did_class === DID_CLASS.VOICE ? PRODUCT_TYPE.phone_number : PRODUCT_TYPE.fax,
        country_code: this.did.country.toLowerCase(),
        did_type: this.did.did_type
      };
    }

    if (this.selection === 'vd') {
      this.initVDInfo();
    }

    this.currentStep = step;
  }

  public onGoBack(step): void {
    this.currentStep = step;
    this.checkboxesData.isValid = false;
  }

  private getSubscription(): void {
    this.loading.subscription = true;
    this.subscriptionService.getSubscription(this.companyId).subscribe((res: SubscriptionModel) => {
      this.subscription = res;
      this.loading.subscription = false;
    }, () => {
      this.loading.subscription = false;
    });
  }

  private initVDInfo(): void {
    this.loading.vd = true;
    forkJoin({
      r: this.verificationDataService.getVerificationDataRequirementsByCountry(this.did.country),
      d: this.didsService.getDidsByCompanyId(this.companyId)
    }).subscribe((res: { r: VerificationDataRequirements, d: DidByCompanyIdResponse[] }) => {
      this.requirements = res.r;
      this.isVDRequired = !!(res.r.BUSINESS || res.r.PRIVATE);
      this.didsWithVerificationData = res.d[0].dids.filter((el: DID) => el.hasOwnProperty('verification_data_id'));
      this.loading.vd = false;
    }, () => {
      this.loading.vd = false;
      this.close()
    })
  }

  private activateDid(): void {
    this.loading.submit = true;
    if(this.planAddonHandling === 'manual') {
      this.didsService.activateDidManually(this.did.id).subscribe((did: DID) => {
        this.finalizeRequest(did.number);
      })
    } else {
      this.didsService.activateDid(this.companyId, this.did.id).subscribe((did: string) => {
        this.finalizeRequest(did);
      });
    }
  }

  private activateWithVDCreation(): void {
    if (this.vdForm.isValid) {
      let payload = this.verificationDataService.buildVerificationData(
        this.companyId,
        this.did.country,
        {
          type: this.vdForm.type,
          data: this.requirements,
          value: this.vdForm
        }
      );
      this.loading.submit = true;
      this.verificationDataService.createVerificationData(payload).pipe(
        concatMap((res: VerificationData) => {
          return this.verificationDataService.linkVerificationData(this.did.id, res.id)
        }),
        concatMap((did: DID) => {
          return this.getActivateDidObservable(did)
        })
      ).subscribe((did: string | DID) => {
        this.loading.submit = false;
        const value: string = this.planAddonHandling === 'manual' ? (<DID>did).number : <string>did;
        this.onEmitValue.emit(value);
        this.close();
      }, () => {
        this.loading.submit = false;
        this.close();
      });
    }
  }

  private activateWithVD(): void {
    this.loading.submit = true;
    this.verificationDataService.linkVerificationData(this.did.id, this.selectedOpt.did.verification_data_id).pipe(
      concatMap((did: DID) => {
        return this.getActivateDidObservable(did)
      })
    ).subscribe((did: string | DID) => {
      const value: string = this.planAddonHandling === 'manual' ? (<DID>did).number : <string>did;
      this.onEmitValue.emit(value);
      this.loading.submit = false;
      this.close();
    }, () => {
      this.loading.submit = false;
      this.close();
    });
  }

  private activateWithPortabilityReq(): void {
    let payload: FormData = this.portabilityService.buildPortabilityData(
      this.companyId,
      this.did,
      this.portabilityDocsForm.files
    );
    this.loading.submit = true;
    this.portabilityService.sendPortabilityRequest(payload).pipe(
      concatMap((res: PortabilityRequestResponse) => {
        return this.getActivateDidObservable(this.did)
      })
    ).subscribe((did: string | DID) => {
      const value: string = this.planAddonHandling === 'manual' ? (<DID>did).number : <string>did;
      this.onEmitValue.emit(value);
      this.loading.submit = false;
      this.close();
    }, () => {
      this.loading.submit = false;
      this.close();
    });
  }

  private finalizeRequest(number: string): void {
    this.onEmitValue.emit(number);
    this.loading.submit = false;
    this.close();
  }

  private getActivateDidObservable(did: DID) {
    return this.planAddonHandling === 'manual' ?
      this.didsService.activateDidManually(did.id) :
      this.didsService.activateDid(this.companyId, did.id)
  }

  public close() {
    this.activeModal.close();
  }

  public ngOnDestroy() {
    if (this.languageSubscription) this.languageSubscription.unsubscribe();
  }
}
